// Small browser guards shared by the instruments. Everything here is safe in jsdom (tests) and never throws.

/** True when the player has asked for reduced motion. */
export function prefersReducedMotion(): boolean {
  try {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/** A design token's current value (canvas drawing needs real colours), or the spec value when the token is missing. */
export function cssToken(name: string, fallback: string): string {
  try {
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return v || fallback;
  } catch {
    return fallback;
  }
}

/** jsdom has no canvas and logs an error when asked for one; tests and real browsers both get a clean answer. */
const NO_CANVAS = typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent ?? '');

/** A 2D context, or null where canvas is not available. */
export function context2d(canvas: HTMLCanvasElement | null | undefined): CanvasRenderingContext2D | null {
  if (!canvas || NO_CANVAS) return null;
  try {
    return canvas.getContext('2d');
  } catch {
    return null;
  }
}

/** The most canvas pixels per CSS pixel: a 3× phone draws one to one; past that nobody sees a difference and memory grows fast. */
export const MAX_RATIO = 3;
/** The longest side any canvas is given, in canvas pixels (mobile browsers blank or refuse much larger ones). */
export const MAX_SIDE = 4096;
/** The most pixels any one canvas holds (8 megapixels, 32 MB), however large the window: well under an iPhone's 16.7 MP limit. */
export const MAX_AREA = 8 * 1024 * 1024;
/** Below this many canvas pixels per CSS pixel a canvas is not worth drawing smaller. */
const MIN_RATIO = 0.25;
/** A change in the ratio smaller than this share is not worth a redraw. */
const RATIO_SLACK = 0.015;

const rawDpr = () => (typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1);

/** Device pixel ratio, capped at MAX_RATIO. */
export function pixelRatio(): number {
  return Math.min(MAX_RATIO, Math.max(1, rawDpr()));
}

/**
 * How much the element's ancestors scale it with CSS transforms (the stage's scale-to-fit, FitGraphic's scale, an entrance
 * animation): its width on the screen over its layout width. 1 when it cannot be told (not laid out, or no layout at all).
 */
export function transformScale(el: Element): number {
  const layout = (el as HTMLElement).offsetWidth;
  if (!layout) return 1;
  const shown = el.getBoundingClientRect().width;
  return Number.isFinite(shown) && shown > 0 ? shown / layout : 1;
}

/**
 * Device pixels per layout (CSS) pixel at which the element really appears: the screen's density times any scale its
 * ancestors apply. A canvas drawn in layout pixels at this ratio lands one to one on the screen's pixels, so it stays crisp
 * when the stage is scaled up to a big window and does not waste pixels when it is scaled down. Uncapped: see canvasRatio.
 */
export function effectiveRatio(el: Element): number {
  return rawDpr() * transformScale(el);
}

/**
 * Canvas pixels per CSS pixel for a canvas of cssW × cssH layout pixels that wants `want` (an effective ratio): at most
 * MAX_RATIO, then lowered so neither side passes MAX_SIDE and the canvas stays within MAX_AREA, so a huge window never
 * allocates a giant canvas. Nonsense in gives 1.
 */
export function canvasRatio(want: number, cssW: number, cssH: number): number {
  let r = Number.isFinite(want) && want > 0 ? Math.min(MAX_RATIO, Math.max(MIN_RATIO, want)) : 1;
  if (cssW > 0 && cssH > 0) r = Math.min(r, MAX_SIDE / Math.max(cssW, cssH), Math.sqrt(MAX_AREA / (cssW * cssH)));
  return r;
}

/**
 * Calls back with the element's effective ratio whenever it has moved by more than a sliver: after a window resize (the stage
 * rescales with the window), when the screen's density changes (the window went to another display or the page was zoomed), and,
 * since nothing announces a change to an ancestor's CSS transform, on a light check every `everyMs`. Returns a disposer.
 */
export function watchEffectiveRatio(el: Element, cb: (ratio: number) => void, everyMs = 500): () => void {
  if (typeof window === 'undefined') return () => undefined;
  let last = effectiveRatio(el);
  const check = () => {
    const now = effectiveRatio(el);
    if (Math.abs(now - last) > RATIO_SLACK * last) { last = now; cb(now); }
  };
  // A resize reaches this listener before the stage has re-rendered at its new scale: look again once it has.
  const timers = new Set<number>();
  const later = (ms: number) => { const id = window.setTimeout(() => { timers.delete(id); check(); }, ms); timers.add(id); };
  const onResize = () => { later(0); later(120); later(400); };
  window.addEventListener('resize', onResize);
  let mql: MediaQueryList | undefined;
  const onDensity = () => { check(); watchDensity(); };
  const watchDensity = () => {
    mql?.removeEventListener?.('change', onDensity);
    mql = typeof window.matchMedia === 'function' ? window.matchMedia(`(resolution: ${rawDpr()}dppx)`) : undefined;
    mql?.addEventListener?.('change', onDensity);
  };
  watchDensity();
  const poll = window.setInterval(check, everyMs);
  return () => {
    window.removeEventListener('resize', onResize);
    mql?.removeEventListener?.('change', onDensity);
    window.clearInterval(poll);
    for (const id of timers) window.clearTimeout(id);
  };
}

/** Calls back with true while the element is on screen and the tab is visible; returns a disposer. */
export function watchVisibility(el: Element, cb: (visible: boolean) => void): () => void {
  let onScreen = true;
  let tabVisible = typeof document === 'undefined' || !document.hidden;
  const emit = () => cb(onScreen && tabVisible);
  const onTab = () => { tabVisible = !document.hidden; emit(); };
  let io: IntersectionObserver | undefined;
  if (typeof IntersectionObserver === 'function') {
    io = new IntersectionObserver((entries) => { for (const e of entries) onScreen = e.isIntersecting; emit(); }, { threshold: 0 });
    io.observe(el);
  }
  document.addEventListener('visibilitychange', onTab);
  emit();
  return () => { io?.disconnect(); document.removeEventListener('visibilitychange', onTab); };
}

/**
 * Calls back with the element's layout width (CSS pixels before any ancestor's transform, so a sheet on the scaled stage is laid
 * out at the stage's own size; effectiveRatio covers the transform) whenever it changes; returns a disposer.
 */
export function watchWidth(el: HTMLElement, cb: (width: number) => void): () => void {
  let last = -1;
  const read = () => { const w = el.offsetWidth; if (w !== last) { last = w; cb(w); } };
  read();
  if (typeof ResizeObserver === 'function') {
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }
  window.addEventListener('resize', read);
  return () => window.removeEventListener('resize', read);
}
